import React from "react";
import { Box, FormLabel, Link, Typography } from "@mui/material";
import { Control, useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RHFCheckboxItem } from "@rentablo/components";
import { CreateApiTokenParamsPermissionsEnum } from "@rentablo/api/apiv1";
import {
  ALL_PERMISSIONS,
  CreateTokenFormValues,
  PERMISSION_TRANSLATION_KEYS,
} from "./constants";

type Props = {
  control: Control<CreateTokenFormValues>;
};

export const PermissionsField = ({ control }: Props) => {
  const { t } = useTranslation();

  const {
    field,
    fieldState: { error },
  } = useController({ control, name: "permissions" });

  const selected = field.value ?? [];
  const allSelected = selected.length === ALL_PERMISSIONS.length;

  const togglePermission = (value: CreateApiTokenParamsPermissionsEnum) => {
    if (selected.includes(value)) {
      field.onChange(selected.filter((permission) => permission !== value));
    } else {
      field.onChange([...selected, value]);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="baseline" justifyContent="space-between">
        <FormLabel>
          <Typography variant="body3" component="span">
            {t("settings.apiAccess.createModal.permissionsLabel")}
          </Typography>
        </FormLabel>
        <Link
          component="button"
          type="button"
          variant="caption"
          onClick={() => field.onChange(allSelected ? [] : ALL_PERMISSIONS)}
        >
          {allSelected
            ? t("settings.apiAccess.createModal.deselectAll")
            : t("settings.apiAccess.createModal.selectAll")}
        </Link>
      </Box>
      <Box
        mt={2}
        sx={{
          border: 1,
          borderColor: "border.secondary",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {ALL_PERMISSIONS.map((permission) => (
          <Box
            key={permission}
            px={4}
            py={2.5}
            sx={{
              "&:not(:last-of-type)": {
                borderBottom: 1,
                borderColor: "border.secondary",
              },
            }}
          >
            <RHFCheckboxItem
              option={{
                value: permission,
                label: (
                  <Box
                    component="span"
                    display="inline-flex"
                    flexWrap="wrap"
                    columnGap={3}
                    alignItems="baseline"
                  >
                    <Typography variant="body3" component="span" minWidth={168}>
                      {permission}
                    </Typography>
                    <Typography
                      variant="body3"
                      component="span"
                      color="textSecondary"
                    >
                      {t(
                        `settings.apiAccess.createModal.permissions.${PERMISSION_TRANSLATION_KEYS[permission]}`
                      )}
                    </Typography>
                  </Box>
                ),
              }}
              checked={selected.includes(permission)}
              onChange={() => togglePermission(permission)}
            />
          </Box>
        ))}
      </Box>
      {error?.message && (
        <Typography variant="caption" color="error.main" component="p" mt={1}>
          {error.message}
        </Typography>
      )}
    </Box>
  );
};
